import React from 'react';
import PopupBase from '../../../popup/base/PopupBase';
import CustomButton from '../../buttons/CustomButton';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import ColorPalette from '../../../colorpalette/ColorPalette';
import { generateReportPDF, buildReportFileName } from '../../../services/clinicalReportService';

/**
 * Generic Share Report Dialog
 * 
 * Reusable dialog for sharing clinical reports.
 * Currently provides PDF generation.
 * Future versions may add additional sharing options.
 * 
 * Any clinical module can use this dialog by providing:
 * - reportData: standardized report data
 * - pdfContentRef: ref to the hidden PDF template element
 * - reportType: string identifier for the file name (e.g., 'assimetria-craniana')
 * - onClose: callback when dialog closes
 *
 * Quando o relatório tem fotos (hasPhotos), o diálogo mostra o controle
 * "Incluir fotos", ligado por padrão: quem decide o que entra no PDF é o
 * módulo dono do template, por includePhotos/onIncludePhotosChange.
 * Enquanto as fotos estão sendo baixadas (preparingPhotos), o botão fica
 * desabilitado, para o PDF nunca sair com imagens em branco.
 */
const ShareReportDialog = ({
    reportData,
    pdfContentRef,
    reportType,
    onClose,
    hasPhotos = false,
    includePhotos = false,
    onIncludePhotosChange,
    preparingPhotos = false,
}) => {

    const waitingPhotos = hasPhotos && includePhotos && preparingPhotos;

    const handleGeneratePDF = () => {
        if (!pdfContentRef?.current) return;

        const fileName = buildReportFileName(
            reportData?.patientName,
            reportType || 'relatorio-clinico'
        );

        generateReportPDF(pdfContentRef.current, fileName);
    };

    const dialogContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0' }}>
            <Typography
                variant="body2"
                sx={{ color: ColorPalette.textMedium, marginBottom: '8px' }}
            >
                Gere um relatório profissional em PDF para compartilhar com outros profissionais de saúde.
            </Typography>

            {hasPhotos && (
                <FormControlLabel
                    control={
                        <Switch
                            checked={includePhotos}
                            onChange={(e) => onIncludePhotosChange && onIncludePhotosChange(e.target.checked)}
                        />
                    }
                    label="Incluir fotos"
                    sx={{ marginLeft: 0, color: ColorPalette.textMedium }}
                />
            )}

            <CustomButton
                variant="contained"
                style="secondary"
                label={waitingPhotos ? "Carregando fotos..." : "Gerar PDF"}
                icon={<PictureAsPdfOutlinedIcon />}
                onClick={handleGeneratePDF}
                enabled={!!pdfContentRef?.current && !waitingPhotos}
            />
        </Box>
    );

    return (
        <PopupBase
            Title="Compartilhar Relatório"
            Content={dialogContent}
            OnClose={onClose}
            maxWidth="xs"
        />
    );
};

export default ShareReportDialog;
